import { BaseController } from 'controllers/BaseController'
import { OnRequestChangeCallback } from 'services/APIs/BaseAPI'
import {
  ChangeChannelRequest,
  GetDraftQuotationQueryResponse,
  OutgoingQuotationDto,
  OutgoingQuotationState,
  SaveOutgoingQuotationCommandRequest,
  SaveOutgoingQuotationCommandResponse,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { InternalAPI } from 'services/APIs/InternalAPI/InternalAPI'
import { GetContext } from 'store/storeUtils'

export type OutgoingQuotation = OutgoingQuotationDto
export { OutgoingQuotationState }

export const outgoingQuotationRequestIds = {
  list: 'ListOutgoingQuotations',
  detail: (quotationId: string) => `GetOutgoingQuotation-${quotationId}`,
  draft: (projectId: string) => `GetOutgoingQuotationDraft-${projectId}`,
  save: (quotationId: string) => `SaveOutgoingQuotation-${quotationId}`,
  send: (quotationId: string) => `SendOutgoingQuotation-${quotationId}`,
  retry: (quotationId: string) => `RetryOutgoingQuotation-${quotationId}`,
  changeChannel: (quotationId: string) =>
    `ChangeOutgoingQuotationChannel-${quotationId}`,
  download: (quotationId: string) => `DownloadOutgoingQuotation-${quotationId}`,
  uploadDecision: 'UploadOutgoingQuotationDecision',
  delete: (quotationId: string) => `DeleteOutgoingQuotation-${quotationId}`,
} as const

export class OutgoingQuotationsClient extends InternalAPI {
  constructor(partyId: string, onRequestChange: OnRequestChangeCallback) {
    super(`/api/parties/${partyId}/quotations/outgoing`, onRequestChange)
  }

  list(): Promise<OutgoingQuotationDto[]> {
    return this.GetAsync({ relativePath: '', id: outgoingQuotationRequestIds.list })
  }

  detail(quotationId: string): Promise<OutgoingQuotationDto> {
    return this.GetAsync({
      relativePath: `/${quotationId}`,
      id: outgoingQuotationRequestIds.detail(quotationId),
    })
  }

  draft(projectId: string): Promise<GetDraftQuotationQueryResponse> {
    return this.GetAsync({
      relativePath: '/draft',
      params: { projectId },
      id: outgoingQuotationRequestIds.draft(projectId),
    })
  }

  save(
    quotationId: string,
    request: SaveOutgoingQuotationCommandRequest,
  ): Promise<SaveOutgoingQuotationCommandResponse> {
    return this.PostAuthenticatedAsync({
      relativePath: `/${quotationId}/save`,
      data: request,
      id: outgoingQuotationRequestIds.save(quotationId),
    })
  }

  send(quotationId: string, projectId: string): Promise<void> {
    return this.PostAuthenticatedAsync({
      relativePath: `/${quotationId}/send`,
      params: { projectId },
      id: outgoingQuotationRequestIds.send(quotationId),
    })
  }

  retry(quotationId: string): Promise<void> {
    return this.PostAuthenticatedAsync({
      relativePath: `/${quotationId}/retry`,
      id: outgoingQuotationRequestIds.retry(quotationId),
    })
  }

  changeChannel(
    quotationId: string,
    request: ChangeChannelRequest,
  ): Promise<OutgoingQuotationDto> {
    return this.PutAuthenticatedAsync({
      relativePath: `/${quotationId}/channel`,
      data: request,
      id: outgoingQuotationRequestIds.changeChannel(quotationId),
    })
  }

  download(quotationId: string) {
    return this.DownloadAsync({
      relativePath: `/${quotationId}/document`,
      id: outgoingQuotationRequestIds.download(quotationId),
    })
  }

  uploadDecision(file: File, quotationId?: string): Promise<unknown> {
    const data = new FormData()
    data.append('file', file)
    if (quotationId) data.append('quotationId', quotationId)
    return this.PostAuthenticatedAsync({
      relativePath: '/decision-upload',
      data,
      id: outgoingQuotationRequestIds.uploadDecision,
    })
  }

  delete(quotationId: string): Promise<void> {
    return this.DeleteAuthenticatedAsync({
      relativePath: `/${quotationId}`,
      id: outgoingQuotationRequestIds.delete(quotationId),
    })
  }
}

export class OutgoingQuotationsController extends BaseController<OutgoingQuotationsClient> {
  constructor() {
    super((onRequestChange) => {
      const { partyId } = GetContext()
      return new OutgoingQuotationsClient(partyId, onRequestChange)
    })
  }

  private handle<T>(operation: () => Promise<T>): Promise<T> {
    return operation().catch((error) => Promise.reject(this.HandleError(error)))
  }

  list() {
    return this.handle(() => this.api.list())
  }

  detail(quotationId: string) {
    return this.handle(() => this.api.detail(quotationId))
  }

  draft(projectId: string) {
    return this.handle(() => this.api.draft(projectId))
  }

  save(quotationId: string, request: SaveOutgoingQuotationCommandRequest) {
    return this.handle(() => this.api.save(quotationId, request))
  }

  send(quotationId: string, projectId: string) {
    return this.handle(() => this.api.send(quotationId, projectId))
  }

  retry(quotationId: string) {
    return this.handle(() => this.api.retry(quotationId))
  }

  changeChannel(quotationId: string, request: ChangeChannelRequest) {
    return this.handle(() => this.api.changeChannel(quotationId, request))
  }

  download(quotationId: string) {
    return this.handle(() => this.api.download(quotationId))
  }

  uploadDecision(file: File, quotationId?: string) {
    return this.handle(() => this.api.uploadDecision(file, quotationId))
  }

  delete(quotationId: string) {
    return this.handle(() => this.api.delete(quotationId))
  }
}
